(function () {
  'use strict';

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#096;');
  }

  function compactCount(value) {
    var count = Math.max(0, Number(value) || 0);
    if (count >= 1000000) return (count / 1000000).toFixed(count >= 10000000 ? 0 : 1).replace(/\.0$/, '') + 'M';
    if (count >= 1000) return (count / 1000).toFixed(count >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return count.toLocaleString('en-GB');
  }

  function pluralCategory(label, count) {
    if (count === 1 || /(?:jeans|trousers|shorts|leggings|clothes)$/i.test(label)) return label;
    if (/[^s]s$/i.test(label)) return label;
    if (/[^aeiou]y$/i.test(label)) return label.slice(0, -1) + 'ies';
    if (/(?:s|x|z|ch|sh)$/i.test(label)) return label + 'es';
    return label + 's';
  }

  function rankingFilterParameter(category) {
    var parameter = String(category && category.filterParameter || 'type').toLowerCase();
    return ['type', 'style', 'vendor', 'size', 'material', 'pattern', 'colour', 'category']
      .indexOf(parameter) !== -1 ? parameter : 'type';
  }

  function rankingDimensionLabel(category) {
    var label = String(category && category.dimensionLabel || '').trim();
    if (label) return label;
    var labels = {
      type: 'Type', style: 'Style', vendor: 'Brand', size: 'Size',
      material: 'Material', pattern: 'Pattern', colour: 'Colour', category: 'Category'
    };
    return labels[rankingFilterParameter(category)] || 'Category';
  }

  function rankingDimensionPlural(category) {
    var label = rankingDimensionLabel(category);
    return /y$/i.test(label) ? label.slice(0, -1) + 'ies' : label + 's';
  }

  function rankingDisplayName(category, count) {
    var name = String(category && category.name || '').trim();
    return rankingFilterParameter(category) === 'type'
      ? pluralCategory(name, count) : name;
  }

  function rankingSearchParameters(category, trending) {
    var parameters = {};
    parameters[rankingFilterParameter(category)] = String(category && category.name || '').trim();
    if (trending) parameters.sort = 'trending';
    return parameters;
  }

  function brandKey(value) {
    var key = String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z0-9]+/g, '');
    if (/^(?:marksandspencer|mands|ms|marksandspencercollection|mandscollection)$/.test(key)) {
      return 'marksandspencer';
    }
    if (/^(?:handm|hm)$/.test(key)) return 'handm';
    if (/^levi(?:s|strauss(?:andco)?)?$/.test(key)) return 'levis';
    return key;
  }

  function brandName(section) {
    return String(section.getAttribute('data-scope-label') ||
      section.getAttribute('data-brand-name') || '').trim();
  }

  function scopeKind(section) {
    return section.getAttribute('data-scope-kind') === 'tag' ? 'tag' : 'brand';
  }

  function scopeValue(section) {
    return String(section.getAttribute('data-scope-value') || brandName(section)).trim();
  }

  function scopeTags(section) {
    if (scopeKind(section) !== 'tag') return [];
    var values = [];
    try {
      var parsed = JSON.parse(section.getAttribute('data-scope-tags') || '[]');
      if (Array.isArray(parsed)) values = parsed;
    } catch (error) {}
    if (!values.length && scopeValue(section)) values = [scopeValue(section)];
    var seen = {};
    return values.map(function (value) {
      return String(value || '').trim();
    }).filter(function (value) {
      var key = value.toLowerCase();
      if (!value || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function applyScope(section, searchParams, includeLabel) {
    var kind = scopeKind(section);
    if (kind === 'tag') {
      var tags = scopeTags(section);
      if (!tags.length) return;
      if (tags.length === 1) {
        searchParams.set('tag', tags[0]);
      } else {
        tags.forEach(function (tag) { searchParams.append('tags[]', tag); });
      }
    } else {
      var value = scopeValue(section);
      if (!value) return;
      searchParams.set(kind, value);
    }
    if (kind === 'tag' && includeLabel !== false && brandName(section)) {
      searchParams.set('tag_label', brandName(section));
    }
  }

  function tagScopeKey(tags) {
    return (Array.isArray(tags) ? tags : []).map(function (tag) {
      return String(tag || '').trim().toLowerCase();
    }).filter(Boolean).sort().join('\u001f');
  }

  function responseMatchesTagScope(payload, requestedTags) {
    if (!payload) return false;
    var responseTags = Array.isArray(payload.tags) ? payload.tags : [];
    if (!responseTags.length && payload.tag) responseTags = [payload.tag];
    return tagScopeKey(responseTags) === tagScopeKey(requestedTags);
  }

  function normaliseTagPart(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  /**
   * Older activity-endpoint builds ignore the tag query parameter. Never let
   * that turn a tag landing page into an unfiltered recent-sales feed. Where
   * the tag namespace has a corresponding indexed event attribute, require an
   * exact value match in the browser as defence in depth. Denim predates the
   * dedicated Fabric Type attribute in the activity payload, so its historic
   * events are recovered only from an explicit Denim title or the Jeans type.
   */
  function eventMatchesTag(event, tag) {
    var raw = String(tag || '').trim();
    var separator = raw.indexOf('_');
    if (!event || separator < 1 || separator >= raw.length - 1) return false;
    var namespace = normaliseTagPart(raw.slice(0, separator));
    var expected = normaliseTagPart(raw.slice(separator + 1));
    var attributes = event.attributes || {};
    var attributeByNamespace = {
      department: 'department',
      category: 'category',
      'navigation category': 'category',
      type: 'type',
      'item type': 'type',
      style: 'style',
      colour: 'colour',
      color: 'colour',
      size: 'size',
      material: 'material',
      pattern: 'pattern',
      'fabric type': 'fabricType'
    };
    var key = attributeByNamespace[namespace];
    if (key && key !== 'fabricType' &&
      normaliseTagPart(attributes[key]) === expected) return true;
    if (namespace === 'fabric type' && expected === 'denim') {
      return normaliseTagPart(attributes.type) === 'jeans' ||
        /(^|[^a-z0-9])denim([^a-z0-9]|$)/i.test(String(event.title || ''));
    }
    return false;
  }

  function eventMatchesTags(event, tags) {
    return Array.isArray(tags) && tags.length > 0 && tags.every(function (tag) {
      return eventMatchesTag(event, tag);
    });
  }

  function renderer() {
    return window.PrewornProductCards &&
      typeof window.PrewornProductCards.renderList === 'function'
      ? window.PrewornProductCards : null;
  }

  function safeImage(value) {
    if (!value) return '';
    try {
      var parsed = new URL(value, window.location.origin);
      return parsed.protocol === 'https:' ? parsed.href : '';
    } catch (error) {
      return '';
    }
  }

  function heroPaletteMix(first, second, amount) {
    amount = Math.max(0, Math.min(1, Number(amount) || 0));
    return {
      r: Math.round(first.r + ((second.r - first.r) * amount)),
      g: Math.round(first.g + ((second.g - first.g) * amount)),
      b: Math.round(first.b + ((second.b - first.b) * amount))
    };
  }

  function heroPaletteShade(colour, factor) {
    factor = Math.max(0, Number(factor) || 0);
    return {
      r: Math.max(0, Math.min(255, Math.round(colour.r * factor))),
      g: Math.max(0, Math.min(255, Math.round(colour.g * factor))),
      b: Math.max(0, Math.min(255, Math.round(colour.b * factor)))
    };
  }

  function heroPaletteBoost(colour, amount) {
    amount = Math.max(1, Number(amount) || 1);
    var average = (colour.r + colour.g + colour.b) / 3;
    return {
      r: Math.max(0, Math.min(255, Math.round(average + ((colour.r - average) * amount)))),
      g: Math.max(0, Math.min(255, Math.round(average + ((colour.g - average) * amount)))),
      b: Math.max(0, Math.min(255, Math.round(average + ((colour.b - average) * amount))))
    };
  }

  function heroPaletteCss(colour) {
    return 'rgb(' + colour.r + ', ' + colour.g + ', ' + colour.b + ')';
  }

  /**
   * Find a representative colour rather than a simple average. Quantised
   * buckets stop pale sky, white studio space, and isolated highlights from
   * washing out the garment and location colours which define the image.
   */
  function dominantHeroColour(pixels, width, height, startRatio, endRatio) {
    var startX = Math.max(0, Math.floor(width * startRatio));
    var endX = Math.min(width, Math.ceil(width * endRatio));
    var buckets = {};
    var best = null;
    var bestChromatic = null;
    for (var y = 0; y < height; y += 2) {
      for (var x = startX; x < endX; x += 2) {
        var offset = ((y * width) + x) * 4;
        var alpha = pixels[offset + 3];
        if (alpha < 220) continue;
        var red = pixels[offset];
        var green = pixels[offset + 1];
        var blue = pixels[offset + 2];
        var maximum = Math.max(red, green, blue);
        var minimum = Math.min(red, green, blue);
        var saturation = maximum - minimum;
        var brightness = (red + green + blue) / 3;
        if (brightness < 14 || (brightness > 232 && saturation < 34)) continue;
        var key = (red >> 5) + ':' + (green >> 5) + ':' + (blue >> 5);
        var weight = 1 + Math.min(2.2, saturation / 70);
        if (brightness > 205) weight *= 0.45;
        var bucket = buckets[key] || { r: 0, g: 0, b: 0, weight: 0, score: 0 };
        bucket.r += red * weight;
        bucket.g += green * weight;
        bucket.b += blue * weight;
        bucket.weight += weight;
        bucket.score += weight;
        buckets[key] = bucket;
        if (!best || bucket.score > best.score) best = bucket;
        if (saturation >= 16) {
          bucket.chromaticScore = (bucket.chromaticScore || 0) + weight;
          if (!bestChromatic || bucket.chromaticScore > bestChromatic.chromaticScore) {
            bestChromatic = bucket;
          }
        }
      }
    }
    // Lifestyle photography often contains more sky, paving, walls, or studio
    // white than clothing. When the image has a genuine colour family, use it
    // as the primary instead of allowing those neutral areas to win by volume.
    if (bestChromatic && bestChromatic.chromaticScore >= 4) best = bestChromatic;
    if (!best || !best.weight) return null;
    return {
      r: Math.round(best.r / best.weight),
      g: Math.round(best.g / best.weight),
      b: Math.round(best.b / best.weight)
    };
  }

  function darkHeroPalette(colour) {
    var deepGreen = { r: 6, g: 30, b: 24 };
    var sampled = colour || deepGreen;
    // Retain the sampled hue. The green anchor is deliberately subtle: it
    // keeps the panels in the Preworn visual family without turning denim
    // blue, rust, or plum photography back into the same forest green.
    var dark = heroPaletteMix(sampled, deepGreen, 0.18);
    var luminance = (dark.r * 0.2126) + (dark.g * 0.7152) + (dark.b * 0.0722);
    if (luminance > 40) dark = heroPaletteShade(dark, 40 / luminance);
    if (luminance < 18) dark = heroPaletteMix(dark, deepGreen, 0.5);
    dark = heroPaletteBoost(dark, 1.55);
    return {
      dark: heroPaletteShade(dark, 0.68),
      mid: heroPaletteShade(dark, 0.88),
      light: heroPaletteShade(dark, 1.08)
    };
  }

  function applyHeroPalette(section, leftSample, rightSample) {
    var left = darkHeroPalette(leftSample);
    var right = darkHeroPalette(rightSample || leftSample);
    section.style.setProperty('--pw-hero-left-dark', heroPaletteCss(left.dark));
    section.style.setProperty('--pw-hero-left-mid', heroPaletteCss(left.mid));
    section.style.setProperty('--pw-hero-left-light', heroPaletteCss(left.light));
    section.style.setProperty('--pw-hero-right-dark', heroPaletteCss(right.dark));
    section.style.setProperty('--pw-hero-right-mid', heroPaletteCss(right.mid));
    section.style.setProperty('--pw-hero-right-light', heroPaletteCss(right.light));
    section.setAttribute('data-pw-hero-palette', 'sampled');
  }

  function sampleHeroPalette(section) {
    var visibleImage = section.querySelector('.pwbrandpage__hero-portrait-image');
    if (!visibleImage || !window.document || typeof window.Image !== 'function') return;
    var source = safeImage(visibleImage.currentSrc || visibleImage.getAttribute('src') || '');
    if (!source) return;
    var sampler = new window.Image();
    sampler.crossOrigin = 'anonymous';
    sampler.decoding = 'async';
    sampler.onload = function () {
      try {
        var canvas = document.createElement('canvas');
        var context = canvas && canvas.getContext && canvas.getContext('2d', { willReadFrequently: true });
        if (!context || !sampler.naturalWidth || !sampler.naturalHeight) return;
        var bounds = visibleImage.getBoundingClientRect ? visibleImage.getBoundingClientRect() : null;
        var displayRatio = bounds && bounds.width && bounds.height
          ? bounds.width / bounds.height : sampler.naturalWidth / sampler.naturalHeight;
        var sourceRatio = sampler.naturalWidth / sampler.naturalHeight;
        var sourceX = 0;
        var sourceY = 0;
        var sourceWidth = sampler.naturalWidth;
        var sourceHeight = sampler.naturalHeight;
        if (sourceRatio > displayRatio) {
          sourceWidth = sampler.naturalHeight * displayRatio;
          sourceX = (sampler.naturalWidth - sourceWidth) / 2;
        } else if (sourceRatio < displayRatio) {
          sourceHeight = sampler.naturalWidth / displayRatio;
          sourceY = (sampler.naturalHeight - sourceHeight) / 2;
        }
        canvas.width = 96;
        canvas.height = 96;
        context.drawImage(sampler, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, 96, 96);
        var pixels = context.getImageData(0, 0, 96, 96).data;
        var left = dominantHeroColour(pixels, 96, 96, 0, 0.48);
        var right = dominantHeroColour(pixels, 96, 96, 0.52, 1);
        if (left || right) applyHeroPalette(section, left || right, right || left);
      } catch (error) {
        // Cross-origin restrictions or older canvas implementations retain
        // the deliberately matched dark-green CSS fallback.
      }
    };
    sampler.src = source;
  }

  function searchHref(section, parameters) {
    var base = String(section.getAttribute('data-search-url') || '/search');
    var url = new URL(base, window.location.origin);
    applyScope(section, url.searchParams, true);
    Object.keys(parameters || {}).forEach(function (key) {
      if (parameters[key] !== '') url.searchParams.set(key, parameters[key]);
    });
    return url.pathname + url.search;
  }

  function requestJsonAttempt(url, attempt, timeoutMs) {
    var requestUrl = new URL(url.toString());
    if (attempt > 0) requestUrl.searchParams.set('_pw_retry', String(Date.now()));
    var controller = typeof window.AbortController === 'function'
      ? new window.AbortController() : null;
    timeoutMs = Math.max(3000, Number(timeoutMs) || 12000);
    var timer = controller ? window.setTimeout(function () {
      controller.abort();
    }, timeoutMs) : null;
    var options = {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: attempt > 0 ? 'reload' : 'default',
      headers: { Accept: 'application/json' }
    };
    if (controller) options.signal = controller.signal;

    return window.fetch(requestUrl.toString(), options).then(function (response) {
      if (!response.ok) {
        var requestError = new Error('HTTP ' + response.status);
        requestError.status = response.status;
        var retryAfter = response.headers && typeof response.headers.get === 'function'
          ? Number(response.headers.get('Retry-After')) || 0 : 0;
        requestError.retryDelay = Math.min(2000, Math.max(350, retryAfter * 1000));
        throw requestError;
      }
      return response.json();
    }).then(function (payload) {
      if (timer) window.clearTimeout(timer);
      return payload;
    }).catch(function (error) {
      if (timer) window.clearTimeout(timer);
      if (attempt < 3) {
        var retryDelay = Math.max(350, Number(error && error.retryDelay) || 350) +
          (attempt * 250);
        return new Promise(function (resolve) {
          window.setTimeout(resolve, retryDelay);
        }).then(function () {
          return requestJsonAttempt(url, attempt + 1, timeoutMs);
        });
      }
      throw error;
    });
  }

  function requestJson(url, timeoutMs) {
    return requestJsonAttempt(url, 0, timeoutMs);
  }

  var BRAND_SIGNAL_CACHE_TTL = 5 * 60 * 1000;
  var BRAND_SIGNAL_STALE_TTL = 24 * 60 * 60 * 1000;

  function brandSignalCacheKey(section) {
    var days = Math.max(1, Math.min(28,
      Number(section.getAttribute('data-new-in-days')) || 7));
    if (scopeKind(section) === 'brand') {
      return 'preworn:brand-signals:v1:' + brandKey(scopeValue(section)) + ':' + days;
    }
    return 'preworn:scope-signals:v8:' + scopeKind(section) + ':' +
      encodeURIComponent(tagScopeKey(scopeTags(section))) + ':' + days;
  }

  function readBrandSignalCache(section) {
    try {
      if (!window.localStorage) return null;
      var raw = window.localStorage.getItem(brandSignalCacheKey(section));
      if (!raw) return null;
      var cached = JSON.parse(raw);
      var age = Date.now() - Math.max(0, Number(cached && cached.savedAt) || 0);
      if (!cached || !cached.payload || age < 0 || age > BRAND_SIGNAL_STALE_TTL) {
        window.localStorage.removeItem(brandSignalCacheKey(section));
        return null;
      }
      return {
        payload: cached.payload,
        fresh: age <= BRAND_SIGNAL_CACHE_TTL
      };
    } catch (error) {
      return null;
    }
  }

  function writeBrandSignalCache(section, payload) {
    try {
      if (!window.localStorage || !payload || payload.source !== 'preworn_catalog_index') return;
      window.localStorage.setItem(brandSignalCacheKey(section), JSON.stringify({
        savedAt: Date.now(),
        payload: payload
      }));
    } catch (error) {}
  }

  function catalogueUrl(section, options) {
    var endpoint = section.getAttribute('data-catalog-api');
    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('action', 'search');
    applyScope(section, url.searchParams, false);
    url.searchParams.set('sort', 'newest');
    url.searchParams.set('new_in_filter_version', '2');
    url.searchParams.set('include_facets', options.includeFacets ? '1' : '0');
    if (options.facetsOnly) url.searchParams.set('facets_only', '1');
    if (options.categoriesOnly) url.searchParams.set('new_in_categories_only', '1');
    if (options.styleDirectoryOnly) {
      url.searchParams.set('style_directory_only', '1');
      url.searchParams.set('style_directory_version', '2');
    }
    if (options.sizeDirectoryOnly) url.searchParams.set('size_directory_only', '1');
    url.searchParams.set('page', String(options.page || 1));
    url.searchParams.set('limit', String(options.limit || 12));
    // Category labels are produced by the catalogue's composite New In
    // expression (department + item type, with product type as a fallback).
    // They are not necessarily literal item_type values: for example,
    // "Womens Dresses" can come from product_type while item_type is empty.
    if (options.category) url.searchParams.set('new_in_category', options.category);
    if (options.newIn) {
      url.searchParams.set('new_in', '1');
      url.searchParams.set('window_days', String(options.windowDays || 7));
    }
    return url;
  }

  function heroMoney(amount, currency) {
    var value = Number(amount);
    if (!isFinite(value)) return '';
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: currency || 'GBP'
      }).format(value);
    } catch (error) {
      return '£' + value.toFixed(2);
    }
  }

  function heroTierPrice(rawPrice) {
    var original = Number(rawPrice);
    if (!isFinite(original) || original <= 0) return null;
    var discountPercent = 55;
    var multiplier = 0.45;
    if (original <= 5) {
      discountPercent = 10;
      multiplier = 0.90;
    } else if (original <= 10) {
      discountPercent = 20;
      multiplier = 0.80;
    } else if (original <= 15) {
      discountPercent = 30;
      multiplier = 0.70;
    } else if (original <= 20) {
      discountPercent = 40;
      multiplier = 0.60;
    } else if (original < 30) {
      discountPercent = 45;
      multiplier = 0.55;
    }
    return {
      original: original,
      discounted: Math.round((original * multiplier) * 100) / 100,
      discountPercent: discountPercent
    };
  }

  function heroLogoHtml(section, vendor) {
    var cards = renderer();
    var base = String(section.getAttribute('data-brand-logo-base') || '');
    if (!cards || typeof cards.brandLogoFilename !== 'function' || !base) return '';
    var filename = cards.brandLogoFilename(vendor || brandName(section));
    if (!filename) return '';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return '<img class="pwbrandpage__hero-product-logo" src="' +
      escapeAttr(base + encodeURIComponent(filename)) +
      '" alt="" aria-hidden="true" loading="eager" onerror="this.hidden=true">';
  }

  function setHeroImages(section, products) {
    var tiles = section.querySelectorAll('[data-pw-brand-hero-tile]');
    var usable = (products || []).filter(function (product) {
      return product && product.featuredImage && safeImage(product.featuredImage.url);
    });
    for (var i = 0; i < tiles.length; i++) {
      var tile = tiles[i];
      var product = usable[i];
      if (!product) continue;
      var image = safeImage(product.featuredImage.url);
      var href = String(product.onlineStoreUrl || (product.handle ? '/products/' + product.handle : '#'));
      var position = String(i + 1).padStart(2, '0');
      var vendor = String(product.vendor || brandName(section)).trim();
      var garment = String(product.attributes && product.attributes.type ||
        product.productType || 'One-off find').trim();
      var categoryTitle = brandKey(garment).indexOf(brandKey(vendor)) === 0
        ? garment : (vendor + ' ' + garment).replace(/\s+/g, ' ').trim();
      var attributes = product.attributes || {};
      var meta = [];
      var size = String(attributes.size || '').trim();
      var colour = String(attributes.colour || '').trim();
      if (size) meta.push('Size ' + size.toUpperCase());
      if (colour) meta.push(colour);
      var price = product.priceRange && product.priceRange.minVariantPrice
        ? product.priceRange.minVariantPrice : null;
      var tier = price ? heroTierPrice(price.amount) : null;
      var currency = price && price.currencyCode ? price.currencyCode : 'GBP';
      var originalPrice = tier ? heroMoney(tier.original, currency) : '';
      var currentPrice = tier ? heroMoney(tier.discounted, currency) : '';
      var saving = tier ? heroMoney(tier.original - tier.discounted, currency) : '';
      tile.innerHTML = '<a href="' + escapeAttr(href) + '" aria-label="View ' +
        escapeAttr(categoryTitle) + '">' +
        '<span class="pwbrandpage__hero-product-media">' +
          heroLogoHtml(section, vendor) +
          '<img class="pwbrandpage__hero-product-image" src="' + escapeAttr(image) +
          '" alt="' + escapeAttr(product.featuredImage.altText || categoryTitle) +
          '" loading="' + (i < 2 ? 'eager' : 'lazy') + '">' +
        '</span>' +
        '<span class="pwbrandpage__hero-product-copy">' +
          '<small>' + position + ' / NEW IN</small>' +
          '<strong>' + escapeHtml(categoryTitle) + '</strong>' +
          (meta.length ? '<span class="pwbrandpage__hero-product-meta">' +
            escapeHtml(meta.join(' · ')) + '</span>' : '') +
          (tier ? '<span class="pwbrandpage__hero-product-price"><s>' +
            escapeHtml(originalPrice) + '</s><b>' + escapeHtml(currentPrice) + '</b></span>' +
            '<span class="pwbrandpage__hero-product-saving">Save ' +
            escapeHtml(saving) + '</span>' : '') +
          '<span class="pwbrandpage__hero-product-action">VIEW PIECE <b aria-hidden="true">↗</b></span>' +
        '</span></a>';
      tile.classList.add('is-loaded');
    }
  }

  function updateMore(section) {
    var state = section._pwBrandProducts;
    var more = section.querySelector('[data-pw-brand-more]');
    if (!state || !more) return;
    var loaded = more.querySelector('[data-pw-brand-loaded]');
    var total = more.querySelector('[data-pw-brand-total]');
    if (loaded) loaded.textContent = state.items.length.toLocaleString('en-GB');
    if (total) total.textContent = state.total.toLocaleString('en-GB');
    // Treat the configured page size as a batch, never as the catalogue
    // ceiling. Some API
    // builds have returned a false hasNextPage flag while still reporting a
    // larger total, so loaded-versus-total is the authoritative fallback.
    state.hasNext = state.hasNext ||
      (state.items.length > 0 && state.items.length < state.total);
    more.hidden = !state.hasNext;
  }

  function renderProductGrid(section, append) {
    var state = section._pwBrandProducts;
    var grid = section.querySelector('[data-pw-brand-new-grid]');
    var cards = renderer();
    if (!state || !grid || !cards) {
      if (grid) grid.setAttribute('aria-busy', 'false');
      return;
    }
    grid.innerHTML = cards.renderList(state.items);
    if (scopeKind(section) === 'tag') normaliseNewCards(grid);
    grid.setAttribute('aria-busy', 'false');
    if (typeof cards.bindWishlist === 'function') cards.bindWishlist(grid);
    if (!append) grid.scrollLeft = 0;
    updateMore(section);
  }

  function renderNewInFilterSelection(target, selected) {
    if (!target) return;
    var buttons = target.querySelectorAll('[data-pw-brand-new-category]');
    for (var i = 0; i < buttons.length; i++) {
      var active = buttons[i].getAttribute('data-pw-brand-new-category') === selected;
      buttons[i].classList.toggle('is-active', active);
      buttons[i].setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  function selectNewInCategory(section, target, category, event) {
    var state = section._pwBrandProducts;
    if (!state || state.loading || category === state.category) return;
    if (target && Date.now() < (target._pwBrandSuppressClickUntil || 0)) {
      if (event) event.preventDefault();
      return;
    }
    state.category = category;
    state.fallback = false;
    state.page = 1;
    state.total = 0;
    state.hasNext = false;
    state.items = [];
    renderNewInFilterSelection(target, category);
    requestProducts(section, 1, false);
  }

  function renderNewInFilters(section, categories, total) {
    var target = section.querySelector('[data-pw-brand-new-filters]');
    if (!target) return;
    var list = Array.isArray(categories) ? categories.filter(function (category) {
      return category && String(category.value || category.label || '').trim() &&
        Number(category.count) > 0;
    }) : [];
    if (!list.length) {
      // A valid rail may already have come from browser cache while the live
      // metrics request was in flight. Never erase it because a later request
      // was temporarily incomplete; the scoped facet fallback will refresh it.
      if (section._pwBrandNewInFiltersReady) {
        target.setAttribute('aria-busy', 'false');
        return true;
      }
      target.innerHTML = '';
      target.hidden = true;
      target.setAttribute('aria-busy', 'false');
      return false;
    }
    // Keep the last valid set so an eager category response can be repainted
    // with the exact total from the product request when that arrives.
    section._pwBrandNewInCategories = list.slice();
    var state = section._pwBrandProducts;
    var selected = state ? state.category : '';
    var brand = brandName(section);
    var upperBrand = brand.toUpperCase();
    var allCount = Math.max(0, Number(total) || 0);
    var markup = '<span class="pwhome__new-in-filters-label">' + escapeHtml(upperBrand) + '</span>' +
      '<button type="button" class="pwhome__new-in-filter' +
        (selected === '' ? ' is-active' : '') + '" data-pw-brand-new-category="" aria-pressed="' +
        (selected === '' ? 'true' : 'false') + '" aria-label="All ' + escapeAttr(brand) +
        ' new in, ' + allCount.toLocaleString('en-GB') + ' products">' +
        '<span class="pwhome__new-in-filter-name">ALL ' + escapeHtml(upperBrand) + '</span>' +
        '<span class="pwhome__new-in-filter-count">' + allCount.toLocaleString('en-GB') + '</span></button>';

    for (var i = 0; i < list.length; i++) {
      var value = String(list[i].value || list[i].label || '').trim();
      var label = String(list[i].label || value).trim();
      var count = Math.max(0, Number(list[i].count) || 0);
      var displayLabel = pluralCategory(label, count).toUpperCase();
      var active = value === selected;
      markup += '<button type="button" class="pwhome__new-in-filter' +
        (active ? ' is-active' : '') + '" data-pw-brand-new-category="' + escapeAttr(value) +
        '" aria-pressed="' + (active ? 'true' : 'false') + '" aria-label="' +
        escapeAttr(brand + ' ' + displayLabel + ', ' + count.toLocaleString('en-GB') +
          (count === 1 ? ' product' : ' products')) + '">' +
        '<span class="pwhome__new-in-filter-name">' + escapeHtml(displayLabel) + '</span>' +
        '<span class="pwhome__new-in-filter-count">' + count.toLocaleString('en-GB') + '</span></button>';
    }

    target.innerHTML = markup;
    target.hidden = false;
    target.setAttribute('aria-busy', 'false');
    section._pwBrandNewInFiltersReady = true;
    bindDragRail(target);
    var buttons = target.querySelectorAll('[data-pw-brand-new-category]');
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].addEventListener('click', function (event) {
        selectNewInCategory(
          section,
          target,
          this.getAttribute('data-pw-brand-new-category') || '',
          event
        );
      });
    }
    return true;
  }

  function newInCategoriesFromFacets(payload) {
    var direct = payload && Array.isArray(payload.newInCategories)
      ? payload.newInCategories : [];
    if (direct.length) {
      return direct.map(function (value) {
        value = value || {};
        var category = String(value.value || value.label || '').trim();
        return {
          value: category,
          label: String(value.label || category).trim(),
          count: Math.max(0, Number(value.count) || 0)
        };
      }).filter(function (value) {
        return value.value && value.count > 0;
      });
    }
    var facets = payload && Array.isArray(payload.facets) ? payload.facets : [];
    for (var i = 0; i < facets.length; i++) {
      var facet = facets[i] || {};
      var id = String(facet.id || '').toLowerCase();
      var label = String(facet.label || '').toLowerCase();
      if (id !== 'item_type' && label !== 'type') continue;
      return (Array.isArray(facet.values) ? facet.values : []).map(function (value) {
        value = value || {};
        var category = String(value.value || value.label || '').trim();
        return {
          value: category,
          label: String(value.label || category).trim(),
          count: Math.max(0, Number(value.count) || 0)
        };
      }).filter(function (value) {
        return value.value && value.count > 0;
      });
    }
    return [];
  }

  function requestNewInFilterFallback(section) {
    var state = section._pwBrandProducts;
    if (section._pwBrandNewInFiltersReady || !state ||
      (state.newInTotalKnown && state.newInTotal < 1)) {
      return Promise.resolve(!!section._pwBrandNewInFiltersReady);
    }
    if (section._pwBrandNewInFiltersRequest) return section._pwBrandNewInFiltersRequest;

    var request = requestJson(catalogueUrl(section, {
      page: 1,
      limit: 1,
      windowDays: state.windowDays,
      newIn: true,
      includeFacets: true,
      facetsOnly: true,
      categoriesOnly: true
    })).then(function (payload) {
      var categories = newInCategoriesFromFacets(payload);
      var categoryTotal = categories.reduce(function (sum, category) {
        return sum + category.count;
      }, 0);
      var responseTotal = Math.max(0, Number(payload && payload.totalCount) || 0);
      var total = state.newInTotalKnown ? state.newInTotal : Math.max(responseTotal, categoryTotal);
      return renderNewInFilters(section, categories, total);
    }).catch(function (error) {
      console.warn('[Preworn Brand] New In categories temporarily unavailable.', error);
      return false;
    }).then(function (result) {
      section._pwBrandNewInFiltersRequest = null;
      return result;
    });
    section._pwBrandNewInFiltersRequest = request;
    return request;
  }

  function renderCategories(section, categories) {
    var grid = section.querySelector('[data-pw-brand-categories]');
    if (!grid) return;
    var markup = '';
    var rendered = 0;
    var list = Array.isArray(categories) ? categories : [];
    var validCategories = [];
    for (var i = 0; i < list.length; i++) {
      var category = list[i] || {};
      var name = String(category.name || '').trim();
      var sold30Days = Math.max(0, Number(category.sold30Days) || 0);
      var availableCount = Math.max(0, Number(category.availableCount) || 0);
      var sold7Days = Math.max(0, Number(category.sold7Days) || 0);
      var soldPrevious7Days = Math.max(0, Number(category.soldPrevious7Days) || 0);
      var productImage = safeImage(category.imageUrl || '');
      if (!name || !sold30Days || !availableCount) continue;
      validCategories.push({
        name: name,
        sold30Days: sold30Days,
        availableCount: availableCount,
        productImage: productImage,
        filterParameter: rankingFilterParameter(category),
        dimensionLabel: rankingDimensionLabel(category),
        sold7Days: sold7Days,
        soldPrevious7Days: soldPrevious7Days,
        trending: sold7Days >= 12 && soldPrevious7Days >= 5 &&
          sold7Days > soldPrevious7Days
      });
    }

    // Match the chart: only its three strongest week-on-week movers receive
    // the card pill, rather than labelling every style with any positive rise.
    var trendingNames = {};
    validCategories.filter(function (category) {
      return category.trending;
    }).sort(function (left, right) {
      var leftScore = left.sold7Days / left.soldPrevious7Days;
      var rightScore = right.sold7Days / right.soldPrevious7Days;
      return rightScore - leftScore || right.sold7Days - left.sold7Days;
    }).slice(0, 3).forEach(function (category) {
      trendingNames[category.name.toLowerCase()] = true;
    });

    var tagSlider = scopeKind(section) === 'tag';
    var renderLimit = tagSlider
      ? Math.min(validCategories.length, 12)
      : (validCategories.length > 9 && validCategories.length < 12
        ? 9
        : Math.min(validCategories.length, 12));
    for (var categoryIndex = 0; categoryIndex < renderLimit; categoryIndex++) {
      var validCategory = validCategories[categoryIndex];
      var name = validCategory.name;
      var sold30Days = validCategory.sold30Days;
      var availableCount = validCategory.availableCount;
      var productImage = validCategory.productImage;
      var trending = !!trendingNames[validCategory.name.toLowerCase()];
      var rank = rendered + 1;
      var displayName = rankingDisplayName(validCategory, availableCount).toUpperCase();
      var dimensionLabel = rankingDimensionLabel(validCategory);
      var imageClass = productImage ? '' : ' pwbrandpage__selling-card--no-image';
      var mediaMarkup = productImage
        ? '<img src="' + escapeAttr(productImage) + '" alt="" loading="lazy" decoding="async">'
        : '<span class="pwbrandpage__selling-placeholder" aria-hidden="true">' +
            '<small>PREWORN</small><strong>' + escapeHtml(displayName) + '</strong>' +
            '<em>' + escapeHtml(dimensionLabel.toUpperCase()) + '</em></span>';
      var layoutClass = tagSlider
        ? 'pwbrandpage__selling-card--slider'
        : (rank <= 3
          ? 'pwbrandpage__selling-card--featured'
          : (rank >= 10 ? 'pwbrandpage__selling-card--wide' : 'pwbrandpage__selling-card--compact'));
      var editorialNotes = [
        'ICONIC STYLE. A BRIGHTER TOMORROW.',
        'TIMELESS COMFORT. REAL IMPACT.',
        'MOVE MORE. WASTE LESS.'
      ];
      var label = 'Shop ' + displayName + ', ' + sold30Days.toLocaleString('en-GB') +
        ' sold in the last 30 days, ' + availableCount.toLocaleString('en-GB') + ' available now';
      markup += '<a class="pwbrandpage__selling-card ' + layoutClass + imageClass +
        (trending ? ' is-trending' : '') + '" href="' +
        escapeAttr(searchHref(section, rankingSearchParameters(validCategory, false))) +
        '" aria-label="' + escapeAttr(label) + '">' +
        '<span class="pwbrandpage__selling-media">' +
          mediaMarkup +
        '</span>' +
        '<span class="pwbrandpage__selling-rank' + (tagSlider && rank > 3
          ? ' pwbrandpage__selling-rank--quiet' : '') + '"><b>#' +
          (tagSlider ? String(rank) : String(rank).padStart(2, '0')) +
          '</b><small>30 DAY</small></span>' +
        (rank === 1 ? '<span class="pwbrandpage__selling-popular">MOST POPULAR</span>' : '') +
        (trending ? '<span class="pwbrandpage__selling-trending">TRENDING</span>' : '') +
        '<span class="pwbrandpage__selling-copy">' +
          '<small>' + escapeHtml(brandName(section).toUpperCase()) + '</small>' +
          '<strong>' + escapeHtml(displayName) + '</strong>' +
          '<span class="pwbrandpage__selling-sales"><b>' + sold30Days.toLocaleString('en-GB') +
          '</b> SOLD · 30 DAYS</span>' +
          '<span class="pwbrandpage__selling-available">' + availableCount.toLocaleString('en-GB') +
          ' AVAILABLE NOW</span>' +
          '<span class="pwbrandpage__selling-action">SHOP ' + escapeHtml(dimensionLabel.toUpperCase()) +
          ' <b aria-hidden="true">↗</b></span>' +
        '</span>' +
        (rank <= 3
          ? '<span class="pwbrandpage__selling-note"><i aria-hidden="true"></i><span>' +
            escapeHtml(editorialNotes[rank - 1]) + '</span></span>'
          : '') +
      '</a>';
      rendered++;
    }
    if (rendered) {
      grid.innerHTML = markup;
    } else {
      grid.innerHTML = '<p class="pwbrandpage__empty">No ' + escapeHtml(brandName(section)) +
        ' sales rankings are available yet.</p>';
    }
    grid.setAttribute('aria-busy', 'false');
    if (tagSlider && rendered) {
      grid.scrollLeft = 0;
      bindDragRail(grid);
    }
  }

  function renderStyleDirectory(section, styles) {
    var directory = section.querySelector('[data-pw-brand-style-directory-section]');
    var track = section.querySelector('[data-pw-brand-style-directory]');
    if (!directory || !track || scopeKind(section) !== 'tag') return false;
    var seen = {};
    var items = (Array.isArray(styles) ? styles : []).map(function (style) {
      style = style || {};
      return {
        name: String(style.name || style.value || style.label || '').trim(),
        availableCount: Math.max(0, Number(style.availableCount || style.count) || 0),
        imageUrl: safeImage(style.imageUrl || ''),
        sampled: style.sampled === true
      };
    }).filter(function (style) {
      var key = style.name.toLowerCase();
      if (!key || !style.availableCount || seen[key]) return false;
      seen[key] = true;
      return true;
    });
    if (items.length < 2) {
      if (section._pwBrandStyleDirectoryReady) return true;
      directory.hidden = true;
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
      return false;
    }

    var styleImages = items.filter(function (style) { return style.imageUrl; });
    var heroImage = section.querySelector('.pwbrandpage__hero-portrait-image');
    var allImageUrl = heroImage
      ? String(heroImage.currentSrc || heroImage.getAttribute('src') || '')
      : '';
    if (!allImageUrl && styleImages.length) {
      allImageUrl = styleImages[styleImages.length - 1].imageUrl;
    }
    var allMedia = allImageUrl
      ? '<img class="pwbrandpage__style-directory-image pwbrandpage__style-directory-image--all" src="' + escapeAttr(allImageUrl) + '" alt="" loading="lazy" decoding="async">'
      : '<span class="pwbrandpage__style-directory-placeholder" aria-hidden="true">ALL</span>';
    var markup = '<a class="pwbrandpage__style-directory-card is-all" role="listitem" href="' +
      escapeAttr(searchHref(section, {})) + '"><span class="pwbrandpage__style-directory-media">' +
      allMedia + '</span><strong>All Styles</strong><small>' +
      items.length.toLocaleString('en-GB') + ' styles</small></a>';

    markup += items.map(function (style) {
      var media = style.imageUrl
        ? '<img class="pwbrandpage__style-directory-image pw-card__product-image" src="' + escapeAttr(style.imageUrl) + '" alt="" loading="lazy" decoding="async">'
        : '<span class="pwbrandpage__style-directory-placeholder" aria-hidden="true">' +
          escapeHtml(style.name.slice(0, 2).toUpperCase()) + '</span>';
      var availabilityLabel = style.sampled
        ? 'Shop style'
        : style.availableCount.toLocaleString('en-GB') + ' available';
      return '<a class="pwbrandpage__style-directory-card" role="listitem" href="' +
        escapeAttr(searchHref(section, { style: style.name })) + '" aria-label="Shop ' +
        escapeAttr(style.name) + (style.sampled ? '' : ', ' +
          style.availableCount.toLocaleString('en-GB') + ' available') +
        '"><span class="pwbrandpage__style-directory-media pw-card__media">' + media +
        '</span><strong>' + escapeHtml(style.name) + '</strong><small>' +
        escapeHtml(availabilityLabel) + '</small></a>';
    }).join('');

    track.innerHTML = markup;
    track.scrollLeft = 0;
    track.setAttribute('aria-busy', 'false');
    directory.hidden = false;
    section._pwBrandStyleDirectoryReady = true;
    section._pwBrandStyleDirectorySampled = items.some(function (style) {
      return style.sampled;
    });
    bindDragRail(track);
    return true;
  }

  function styleDirectoryFromProducts(products) {
    var groups = {};
    (Array.isArray(products) ? products : []).forEach(function (product) {
      product = product || {};
      var attributes = product.attributes || {};
      var name = String(attributes.style || product.style || '').trim();
      var key = name.toLowerCase();
      if (!key || /^(?:other|unknown|n\/a)$/i.test(name)) return;
      var imageUrl = product.featuredImage
        ? safeImage(product.featuredImage.url || '') : '';
      if (!groups[key]) {
        groups[key] = {
          name: name,
          availableCount: 0,
          imageUrl: imageUrl,
          sampled: true
        };
      }
      groups[key].availableCount += 1;
      if (!groups[key].imageUrl && imageUrl) groups[key].imageUrl = imageUrl;
    });
    return Object.keys(groups).map(function (key) {
      return groups[key];
    }).sort(function (left, right) {
      return right.availableCount - left.availableCount ||
        left.name.localeCompare(right.name, 'en-GB');
    });
  }

  var STYLE_DIRECTORY_CACHE_TTL = 15 * 60 * 1000;
  var STYLE_DIRECTORY_STALE_TTL = 24 * 60 * 60 * 1000;

  function styleDirectoryCacheKey(section) {
    return 'preworn:style-directory:v1:' +
      encodeURIComponent(tagScopeKey(scopeTags(section)));
  }

  function readStyleDirectoryCache(section) {
    try {
      if (!window.localStorage) return null;
      var raw = window.localStorage.getItem(styleDirectoryCacheKey(section));
      if (!raw) return null;
      var cached = JSON.parse(raw);
      var age = Date.now() - Math.max(0, Number(cached && cached.savedAt) || 0);
      if (!cached || !Array.isArray(cached.styles) || age < 0 ||
          age > STYLE_DIRECTORY_STALE_TTL) {
        window.localStorage.removeItem(styleDirectoryCacheKey(section));
        return null;
      }
      return { styles: cached.styles, fresh: age <= STYLE_DIRECTORY_CACHE_TTL };
    } catch (error) {
      return null;
    }
  }

  function writeStyleDirectoryCache(section, styles) {
    try {
      if (!window.localStorage || !Array.isArray(styles) || styles.length < 2) return;
      window.localStorage.setItem(styleDirectoryCacheKey(section), JSON.stringify({
        savedAt: Date.now(),
        styles: styles
      }));
    } catch (error) {}
  }

  function requestStyleDirectory(section) {
    if (scopeKind(section) !== 'tag') return Promise.resolve(false);
    if (section._pwBrandStyleDirectoryRequest) {
      return section._pwBrandStyleDirectoryRequest;
    }

    var cached = readStyleDirectoryCache(section);
    var cachedResult = cached ? renderStyleDirectory(section, cached.styles) : false;
    if (cached && cached.fresh) return Promise.resolve(cachedResult);

    var request = requestJson(catalogueUrl(section, {
      page: 1,
      limit: 12,
      includeFacets: false,
      facetsOnly: true,
      styleDirectoryOnly: true
    }), 15000).then(function (payload) {
      if (!payload || payload.source !== 'preworn_catalog_index' ||
          !Array.isArray(payload.styleDirectory)) {
        throw new Error('Invalid style directory response');
      }
      writeStyleDirectoryCache(section, payload.styleDirectory);
      return renderStyleDirectory(section, payload.styleDirectory);
    }).catch(function (error) {
      console.warn('[Preworn Brand] Style directory temporarily unavailable.', error);
      if (!cached) renderStyleDirectory(section, []);
      return cachedResult;
    }).then(function (result) {
      section._pwBrandStyleDirectoryRequest = null;
      return result;
    });
    section._pwBrandStyleDirectoryRequest = request;
    return request;
  }

  function sizeDirectoryFromPayload(payload) {
    if (payload && Array.isArray(payload.sizeDirectory)) {
      return payload.sizeDirectory;
    }
    var facets = payload && Array.isArray(payload.facets) ? payload.facets : [];
    for (var i = 0; i < facets.length; i++) {
      var facet = facets[i] || {};
      if (String(facet.id || '').toLowerCase() !== 'size_value' &&
          String(facet.label || '').toLowerCase() !== 'size') continue;
      return Array.isArray(facet.values) ? facet.values : [];
    }
    return [];
  }

  function sizeSortValue(value) {
    var label = String(value || '').trim().toUpperCase();
    var uk = label.match(/^UK\s*(\d+(?:\.\d+)?)$/);
    if (uk) return Number(uk[1]);
    var alpha = {
      XXS: 200, XS: 210, S: 220, M: 230, L: 240,
      XL: 250, XXL: 260, XXXL: 270, XXXXL: 280, 'ONE SIZE': 900
    };
    return alpha[label] !== undefined ? alpha[label] : 500;
  }

  function renderSizeDirectory(section, sizes) {
    var shell = section.querySelector('[data-pw-brand-size-shop]');
    var target = section.querySelector('[data-pw-brand-size-pills]');
    if (!shell || !target) return false;
    var list = (Array.isArray(sizes) ? sizes : []).map(function (size) {
      size = size || {};
      return {
        value: String(size.value || size.name || '').trim(),
        label: String(size.label || size.value || size.name || '').trim(),
        count: Math.max(0, Number(size.count || size.availableCount) || 0)
      };
    }).filter(function (size) {
      return size.value && size.count > 0;
    }).sort(function (left, right) {
      var difference = sizeSortValue(left.label) - sizeSortValue(right.label);
      return difference || left.label.localeCompare(right.label, 'en-GB', { numeric: true });
    });
    if (!list.length) {
      shell.hidden = true;
      shell.setAttribute('aria-busy', 'false');
      target.innerHTML = '';
      return false;
    }
    target.innerHTML = list.map(function (size) {
      var label = size.label.replace(/^uk\s*/i, 'UK ');
      return '<a href="' + escapeAttr(searchHref(section, { size: size.value })) +
        '" aria-label="Shop ' + escapeAttr(brandName(section)) + ' in ' +
        escapeAttr(label) + ', ' + size.count.toLocaleString('en-GB') +
        ' available"><span>' + escapeHtml(label) + '</span></a>';
    }).join('');
    shell.hidden = false;
    shell.setAttribute('aria-busy', 'false');
    return true;
  }

  function bindSizeRailMouseScroll(section) {
    var rail = section.querySelector('[data-pw-brand-size-pills]');
    if (!rail || rail.getAttribute('data-mouse-scroll-ready') === 'true') return;
    rail.setAttribute('data-mouse-scroll-ready', 'true');
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScrollLeft = 0;

    rail.addEventListener('wheel', function (event) {
      if (rail.scrollWidth <= rail.clientWidth) return;
      var delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX : event.deltaY;
      if (!delta) return;
      if (event.deltaMode === 1) delta *= 16;
      else if (event.deltaMode === 2) delta *= rail.clientWidth;
      var maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      var next = Math.max(0, Math.min(maximum, rail.scrollLeft + delta));
      if (Math.abs(next - rail.scrollLeft) < 0.5) return;
      event.preventDefault();
      rail.scrollLeft = next;
    }, { passive: false });

    rail.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 ||
          rail.scrollWidth <= rail.clientWidth) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = rail.scrollLeft;
      rail.classList.add('is-mouse-dragging');
      if (rail.setPointerCapture) rail.setPointerCapture(event.pointerId);
    });

    rail.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var distance = event.clientX - startX;
      if (Math.abs(distance) > 4) moved = true;
      if (!moved) return;
      event.preventDefault();
      rail.scrollLeft = startScrollLeft - distance;
    });

    function finishMouseDrag(event) {
      if (!dragging) return;
      dragging = false;
      rail.classList.remove('is-mouse-dragging');
      if (rail.releasePointerCapture && rail.hasPointerCapture &&
          rail.hasPointerCapture(event.pointerId)) {
        rail.releasePointerCapture(event.pointerId);
      }
      if (moved) {
        rail._pwSuppressSizeClick = true;
        window.setTimeout(function () { rail._pwSuppressSizeClick = false; }, 0);
      }
    }

    rail.addEventListener('pointerup', finishMouseDrag);
    rail.addEventListener('pointercancel', finishMouseDrag);
    rail.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
    rail.addEventListener('click', function (event) {
      if (!rail._pwSuppressSizeClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  }

  function requestSizeDirectory(section) {
    if (scopeKind(section) !== 'tag') return Promise.resolve(false);
    return requestJson(catalogueUrl(section, {
      page: 1,
      limit: 12,
      includeFacets: true,
      facetsOnly: true,
      sizeDirectoryOnly: true
    }), 12000).then(function (payload) {
      if (!payload || payload.source !== 'preworn_catalog_index') {
        throw new Error('Invalid size directory response');
      }
      return renderSizeDirectory(section, sizeDirectoryFromPayload(payload));
    }).catch(function (error) {
      console.warn('[Preworn Brand] Shop by Size temporarily unavailable.', error);
      return false;
    });
  }

  function renderTrendSummary(section, categories) {
    var summary = section.querySelector('[data-pw-brand-trend-summary]');
    var risingList = section.querySelector('[data-pw-brand-rising-list]');
    var todayList = section.querySelector('[data-pw-brand-most-today]');
    var graphLabels = section.querySelector('[data-pw-brand-trend-graph-labels]');
    var leaderboardHeading = section.querySelector('[data-pw-brand-leaderboard-heading]');
    var leaderboardContext = section.querySelector('[data-pw-brand-leaderboard-context]');
    var trendIntro = section.querySelector('[data-pw-brand-trend-intro]');
    if (!summary || !risingList || !todayList || !Array.isArray(categories)) return;

    var normalized = categories.map(function (category) {
      category = category || {};
      var sold7Days = Math.max(0, Number(category.sold7Days) || 0);
      var soldPrevious7Days = Math.max(0, Number(category.soldPrevious7Days) || 0);
      return {
        name: String(category.name || '').trim(),
        filterParameter: rankingFilterParameter(category),
        dimensionLabel: rankingDimensionLabel(category),
        availableCount: Math.max(0, Number(category.availableCount) || 0),
        soldToday: Math.max(0, Number(category.soldToday) || 0),
        sold30Days: Math.max(0, Number(category.sold30Days) || 0),
        sold7Days: sold7Days,
        soldPrevious7Days: soldPrevious7Days,
        trendScore: soldPrevious7Days ? sold7Days / soldPrevious7Days : 0,
        trendChangePercent: soldPrevious7Days
          ? Math.round(((sold7Days - soldPrevious7Days) / soldPrevious7Days) * 100)
          : 0
      };
    }).filter(function (category) {
      return category.name && category.availableCount > 0;
    });

    var rising = normalized.filter(function (category) {
      return category.sold7Days >= 12 && category.soldPrevious7Days >= 5 &&
        category.trendScore > 1;
    }).sort(function (left, right) {
      return right.trendScore - left.trendScore || right.sold7Days - left.sold7Days;
    }).slice(0, 3);

    var isMomentumFallback = false;
    if (!rising.length) {
      isMomentumFallback = true;
      rising = normalized.slice().sort(function (left, right) {
        return right.sold7Days - left.sold7Days;
      }).slice(0, 3);
    }

    var mostToday = normalized.filter(function (category) {
      return category.soldToday > 0;
    }).sort(function (left, right) {
      return right.soldToday - left.soldToday || right.sold7Days - left.sold7Days;
    }).slice(0, 4);

    var most30Days = normalized.filter(function (category) {
      return category.sold30Days > 0;
    }).sort(function (left, right) {
      return right.sold30Days - left.sold30Days || right.sold7Days - left.sold7Days;
    }).slice(0, 4);

    var todayVolume = mostToday.reduce(function (total, category) {
      return total + category.soldToday;
    }, 0);
    var showToday = mostToday.length >= 3 && todayVolume >= 12;
    var leaderboard = showToday ? mostToday : most30Days;

    if (!rising.length && !leaderboard.length) return;
    if (leaderboardHeading) {
      leaderboardHeading.textContent = showToday ? 'MOST SOLD TODAY' : 'MOST SOLD · 30 DAYS';
    }
    if (leaderboardContext) {
      leaderboardContext.textContent = showToday
        ? 'UPDATED LIVE'
        : (scopeKind(section) === 'tag'
          ? rankingDimensionLabel(normalized[0]).toUpperCase() + ' DEMAND'
          : 'BRAND DEMAND');
    }
    if (trendIntro && normalized.length && scopeKind(section) === 'tag') {
      trendIntro.textContent = 'The ' + brandName(section) + ' ' +
        rankingDimensionPlural(normalized[0]).toLowerCase() +
        ' gaining momentum right now, based on week-on-week sales movement.';
      risingList.setAttribute('aria-label', brandName(section) + ' ' +
        rankingDimensionPlural(normalized[0]).toLowerCase() + ' rising fastest');
    }

    risingList.innerHTML = rising.map(function (category, index) {
      var change = isMomentumFallback
        ? category.sold7Days.toLocaleString('en-GB') + ' SOLD THIS WEEK'
        : '+' + category.trendChangePercent.toLocaleString('en-GB') + '% VS LAST WEEK';
      var comparison = category.soldPrevious7Days
        ? category.sold7Days.toLocaleString('en-GB') + ' SOLD THIS WEEK · ' +
          category.soldPrevious7Days.toLocaleString('en-GB') + ' LAST WEEK'
        : category.sold7Days.toLocaleString('en-GB') + ' SOLD THIS WEEK';
      return '<a class="pwhome__trend-card" href="' +
        escapeAttr(searchHref(section, rankingSearchParameters(category, true))) + '">' +
        '<small>' + String(index + 1).padStart(2, '0') + '</small>' +
        '<span class="pwhome__trend-card-copy"><strong>' + escapeHtml(category.name) + '</strong>' +
          '<span>' + escapeHtml(comparison) + '</span></span>' +
        '<em>' + escapeHtml(change) + '</em><i aria-hidden="true">↗</i></a>';
    }).join('');

    if (graphLabels) {
      graphLabels.innerHTML = rising.map(function (category, index) {
        var value = isMomentumFallback
          ? category.sold7Days.toLocaleString('en-GB')
          : '+' + category.trendChangePercent.toLocaleString('en-GB') + '%';
        return '<span class="pwhome__trend-graph-label pwhome__trend-graph-label--' + (index + 1) + '">' +
          '<strong><b>#' + (index + 1) + '</b><span>' + escapeHtml(category.name) + '</span>' +
          '<em>' + escapeHtml(value) + '</em></strong></span>';
      }).join('');
    }

    todayList.innerHTML = leaderboard.map(function (category, index) {
      var count = showToday ? category.soldToday : category.sold30Days;
      return '<a href="' + escapeAttr(searchHref(section, rankingSearchParameters(category, true))) + '">' +
        '<small>#' + (index + 1) + '</small><strong>' + escapeHtml(category.name) + '</strong>' +
        '<b>' + count.toLocaleString('en-GB') + '</b></a>';
    }).join('');

    summary.hidden = false;
  }

  function rankingViews(payload) {
    var rawViews = payload && Array.isArray(payload.rankingViews)
      ? payload.rankingViews : [];
    if (!rawViews.length && payload && Array.isArray(payload.popularCategories)) {
      rawViews = [{
        dimension: payload.rankingDimension || {},
        categories: payload.popularCategories
      }];
    }
    return rawViews.map(function (view) {
      view = view || {};
      var categories = Array.isArray(view.categories) ? view.categories : [];
      var dimension = view.dimension || {};
      var example = categories[0] || {
        filterParameter: dimension.parameter,
        dimensionLabel: dimension.label
      };
      return {
        key: rankingFilterParameter(example),
        label: rankingDimensionPlural(example),
        categories: categories
      };
    }).filter(function (view, index, views) {
      return view.categories.length > 0 && views.findIndex(function (candidate) {
        return candidate.key === view.key;
      }) === index;
    });
  }

  function renderRankingTabs(section) {
    var tabLists = section.querySelectorAll
      ? section.querySelectorAll('[data-pw-brand-ranking-tabs]') : [];
    var views = Array.isArray(section._pwBrandRankingViews)
      ? section._pwBrandRankingViews : [];
    if (!tabLists || !tabLists.length) return;
    if (scopeKind(section) !== 'tag' || !views.length) {
      for (var emptyIndex = 0; emptyIndex < tabLists.length; emptyIndex++) {
        tabLists[emptyIndex].hidden = true;
        tabLists[emptyIndex].innerHTML = '';
      }
      return;
    }
    var sectionId = String(section.getAttribute('data-section-id') || 'scope');
    var controlledPanels = 'PrewornBrandTrends-' + sectionId +
      ' PrewornBrandRankings-' + sectionId;
    var markup = '<span>TREND BY</span>' + views.map(function (view) {
      var active = view.key === section._pwBrandRankingView;
      return '<button type="button" role="tab" data-pw-brand-ranking-view="' +
        escapeAttr(view.key) + '" aria-selected="' + (active ? 'true' : 'false') +
        '" aria-controls="' + escapeAttr(controlledPanels) + '" tabindex="' +
        (active ? '0' : '-1') + '" class="' + (active ? 'is-active' : '') + '">' +
        escapeHtml(view.label.toUpperCase()) + '</button>';
    }).join('');
    for (var tabIndex = 0; tabIndex < tabLists.length; tabIndex++) {
      tabLists[tabIndex].innerHTML = markup;
      tabLists[tabIndex].hidden = false;
    }
  }

  function editorialRankingSubject(section) {
    var subject = brandName(section).replace(
      /^(?:women[\u2019']?s|men[\u2019']?s|kids[\u2019']?|boys|girls)\s+/i,
      ''
    );
    var singular = {
      blouses: 'Blouse', skirts: 'Skirt', dresses: 'Dress', shirts: 'Shirt',
      jackets: 'Jacket', coats: 'Coat', jumpers: 'Jumper', hoodies: 'Hoodie',
      sweatshirts: 'Sweatshirt', joggers: 'Jogger', trousers: 'Trouser',
      shorts: 'Short', tops: 'Top'
    };
    var words = subject.split(/\s+/);
    var last = String(words[words.length - 1] || '').toLowerCase();
    if (singular[last]) words[words.length - 1] = singular[last];
    return words.join(' ') || brandName(section);
  }

  function selectRankingView(section, key) {
    var views = Array.isArray(section._pwBrandRankingViews)
      ? section._pwBrandRankingViews : [];
    var selected = views.filter(function (view) { return view.key === key; })[0] || views[0];
    if (!selected) return false;
    section._pwBrandRankingView = selected.key;
    renderRankingTabs(section);
    renderCategories(section, selected.categories);
    renderTrendSummary(section, selected.categories);
    var boardHeading = section.querySelector('[data-pw-brand-ranking-board-heading]');
    var boardLink = section.querySelector('[data-pw-brand-ranking-board-link]');
    if (boardHeading && scopeKind(section) === 'tag') {
      boardHeading.innerHTML = '<span class="pwbrandpage__selling-board-prefix">TRENDING:</span> TOP ' +
        escapeHtml(editorialRankingSubject(section).toUpperCase()) + ' ' +
        escapeHtml(selected.label.toUpperCase()) + ' RIGHT NOW';
    }
    if (boardLink && scopeKind(section) === 'tag') {
      boardLink.innerHTML = 'EXPLORE TRENDING ' + escapeHtml(selected.label.toUpperCase()) +
        ' <span aria-hidden="true">→</span>';
    }
    return true;
  }

  function mergeRankingViews(currentViews, incomingViews) {
    var merged = [];
    var positions = Object.create(null);
    incomingViews.concat(currentViews).forEach(function (view) {
      if (!view || !view.key || positions[view.key] !== undefined) return;
      positions[view.key] = merged.length;
      merged.push(view);
    });
    var priority = ['style', 'vendor', 'type', 'colour', 'size', 'material', 'pattern', 'category'];
    return merged.sort(function (left, right) {
      var leftIndex = priority.indexOf(left.key);
      var rightIndex = priority.indexOf(right.key);
      leftIndex = leftIndex < 0 ? priority.length : leftIndex;
      rightIndex = rightIndex < 0 ? priority.length : rightIndex;
      return leftIndex - rightIndex;
    });
  }

  function applyRankingViews(section, payload, replaceExisting) {
    var incomingViews = rankingViews(payload);
    var currentViews = Array.isArray(section._pwBrandRankingViews)
      ? section._pwBrandRankingViews : [];
    var views = replaceExisting
      ? incomingViews : mergeRankingViews(currentViews, incomingViews);
    section._pwBrandRankingViews = views;
    var current = !section._pwBrandRankingInteracted
      ? (views[0] ? views[0].key : '')
      : (views.some(function (view) {
          return view.key === section._pwBrandRankingView;
        }) ? section._pwBrandRankingView : (views[0] ? views[0].key : ''));
    if (!current) {
      renderRankingTabs(section);
      renderCategories(section, []);
      return false;
    }
    return selectRankingView(section, current);
  }

  function requestRankingViews(section) {
    if (scopeKind(section) !== 'tag') return Promise.resolve(false);
    var endpoint = section.getAttribute('data-catalog-api');
    if (!endpoint) return Promise.resolve(false);
    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('action', 'tag');
    applyScope(section, url.searchParams, false);
    url.searchParams.set('label', brandName(section));
    url.searchParams.set('window_days', String(
      Math.max(1, Math.min(28, Number(section.getAttribute('data-new-in-days')) || 7))
    ));
    url.searchParams.set('ranking_views_only', '1');
    // The cached page payload already supplies its available Style/Pattern
    // views. Fetch Brand independently so one focused query cannot be held
    // behind every other 30-day aggregation on a cold cache.
    url.searchParams.set('ranking_dimension', 'vendor');
    url.searchParams.set('ranking_views_version', '2');
    return requestJson(url, 30000).then(function (payload) {
      if (!payload || payload.source !== 'preworn_catalog_index' ||
          !responseMatchesTagScope(payload, scopeTags(section))) {
        throw new Error('Invalid scoped ranking response');
      }
      return applyRankingViews(section, payload, false);
    }).catch(function (error) {
      console.warn('[Preworn Brand] Dedicated ranking views unavailable; retaining available view.', error);
      renderRankingTabs(section);
      return false;
    });
  }

  function setMetric(section, selector, value) {
    var targets = section.querySelectorAll ? section.querySelectorAll(selector) : [];
    if (!targets || !targets.length) {
      var fallback = section.querySelector(selector);
      targets = fallback ? [fallback] : [];
    }
    for (var i = 0; i < targets.length; i++) targets[i].textContent = compactCount(value);
  }

  function updateSignals(section, payload) {
    if (!payload || payload.source !== 'preworn_catalog_index') return false;
    section._pwBrandSignals = payload;
    var state = section._pwBrandProducts;
    var newInCount = state && state.newInTotalKnown ? state.newInTotal : payload.newInCount;
    setMetric(section, '[data-pw-brand-available]', payload.availableCount);
    setMetric(section, '[data-pw-brand-new]', newInCount);
    setMetric(section, '[data-pw-brand-sold]', payload.sold30Days);
    renderNewInFilters(section, payload.newInCategories, payload.newInCount);
    var styleDirectory = Array.isArray(payload.styleDirectory)
      ? payload.styleDirectory : [];
    if (!styleDirectory.length) {
      var styleView = rankingViews(payload).filter(function (view) {
        return view.key === 'style';
      })[0];
      styleDirectory = styleView ? styleView.categories : [];
    }
    // A late partial metrics response must not clear a style rail already
    // painted by the dedicated first-load availability request.
    if (styleDirectory.length) renderStyleDirectory(section, styleDirectory);
    var hasCatalogueCategories = applyRankingViews(section, payload);
    var hasActivitySignals = updateTagSignalsFromActivity(
      section,
      section._pwTagActivityPayload,
      !!section._pwTagActivityPayload
    );
    if (hasCatalogueCategories || hasActivitySignals) {
      section._pwBrandCategoriesReady = true;
    }
    return hasCatalogueCategories || hasActivitySignals;
  }

  function updateTagSignalsFromActivity(section, payload, responseIsScoped) {
    if (scopeKind(section) !== 'tag' || !responseIsScoped || !payload) return false;
    section._pwTagActivityPayload = payload;
    var stats = payload.stats || {};
    var availableCount = Math.max(0, Number(payload.available_count) || 0);
    var sold30Days = Math.max(0, Number(stats.items_30_days) || 0);
    var categories = Array.isArray(payload.popular_categories)
      ? payload.popular_categories : [];
    if (availableCount > 0) {
      setMetric(section, '[data-pw-brand-available]', availableCount);
    }
    if (sold30Days > 0) {
      setMetric(section, '[data-pw-brand-sold]', sold30Days);
    }
    var catalogueViews = Array.isArray(section._pwBrandRankingViews)
      ? section._pwBrandRankingViews : [];
    if (categories.length && !catalogueViews.length) {
      applyRankingViews(section, { popularCategories: categories });
      section._pwBrandCategoriesReady = true;
    }
    return availableCount > 0 || sold30Days > 0 || categories.length > 0;
  }

  function requestSignals(section) {
    var endpoint = section.getAttribute('data-catalog-api');
    if (!endpoint) return Promise.resolve();
    var cached = readBrandSignalCache(section);
    var cachedResult = false;
    if (cached) {
      cachedResult = updateSignals(section, cached.payload);
      if (cached.fresh) return Promise.resolve(cachedResult);
    }
    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('action', scopeKind(section));
    applyScope(section, url.searchParams, false);
    if (scopeKind(section) === 'tag') url.searchParams.set('label', brandName(section));
    url.searchParams.set('window_days', String(
      Math.max(1, Math.min(28, Number(section.getAttribute('data-new-in-days')) || 7))
    ));
    url.searchParams.set('metrics_version', '6');
    return requestJson(url, scopeKind(section) === 'tag' ? 30000 : 12000).then(function (payload) {
      writeBrandSignalCache(section, payload);
      return updateSignals(section, payload);
    }).catch(function (error) {
      if (cached) {
        console.warn('[Preworn Brand] Refresh unavailable; using cached brand metrics.', error);
        return cachedResult;
      }
      if (section._pwBrandCategoriesReady ||
          (Array.isArray(section._pwBrandRankingViews) && section._pwBrandRankingViews.length)) {
        console.warn('[Preworn Brand] Catalogue metrics unavailable; retaining activity rankings.', error);
        return true;
      }
      renderCategories(section, []);
      console.warn('[Preworn Brand] Live brand metrics unavailable.', error);
      return false;
    });
  }

  function productStatus(section, message) {
    var status = section.querySelector('[data-pw-brand-new-status]');
    if (status) status.textContent = message;
  }

  function applyProductPayload(section, payload, append) {
    var state = section._pwBrandProducts;
    if (!payload || payload.source !== 'preworn_catalog_index' || !Array.isArray(payload.items)) {
      throw new Error('Invalid catalogue response');
    }
    state.page = Math.max(1, Number(payload.page) || 1);
    state.total = Math.max(0, Number(payload.totalCount) || 0);
    if (!state.fallback && !state.category) {
      state.newInTotal = state.total;
      state.newInTotalKnown = true;
      setMetric(section, '[data-pw-brand-new]', state.newInTotal);
      if (section._pwBrandNewInFiltersReady &&
        Array.isArray(section._pwBrandNewInCategories)) {
        renderNewInFilters(section, section._pwBrandNewInCategories, state.newInTotal);
      }
    }
    state.items = append ? state.items.concat(payload.items) : payload.items.slice();
    state.hasNext = payload.items.length > 0 &&
      (payload.hasNextPage === true || state.items.length < state.total);
    if (scopeKind(section) === 'tag' &&
        (!section._pwBrandStyleDirectoryReady || section._pwBrandStyleDirectorySampled)) {
      var inferredStyles = styleDirectoryFromProducts(state.items);
      if (inferredStyles.length >= 2) renderStyleDirectory(section, inferredStyles);
    }
    renderProductGrid(section, append);
    if (!append) {
      if (!section._pwBrandCategoriesReady) renderCategories(section, []);
    }
    productStatus(section, state.total
      ? (state.category
        ? state.total.toLocaleString('en-GB') + ' available ' + brandName(section) +
          ' ' + pluralCategory(state.category, state.total).toLowerCase() + ', newest first.'
        : (state.fallback ? 'No new arrivals in the selected window — showing the latest available pieces.' :
          state.total.toLocaleString('en-GB') + ' new ' + brandName(section) + ' pieces in the last ' + state.windowDays + ' days.'))
      : 'No available ' + brandName(section) + ' pieces were found.');
  }

  function requestProducts(section, page, append) {
    var state = section._pwBrandProducts;
    if (!state || state.loading) return Promise.resolve();
    state.loading = true;
    var button = section.querySelector('[data-pw-brand-load-more]');
    if (button) button.disabled = true;
    return requestJson(catalogueUrl(section, {
      page: page,
      limit: state.limit,
      windowDays: state.windowDays,
      category: state.category,
      // Category pills and their counts describe this exact New In window.
      // Keep category requests in that window and use new_in_category so
      // composite labels such as "Womens Dresses" match the API expression.
      newIn: !state.fallback
    })).then(function (payload) {
      if (!append && !state.fallback && !state.category && Number(payload.totalCount) === 0) {
        state.newInTotal = 0;
        state.newInTotalKnown = true;
        setMetric(section, '[data-pw-brand-new]', 0);
        state.fallback = true;
        state.loading = false;
        return requestProducts(section, 1, false);
      }
      applyProductPayload(section, payload, append);
    }).catch(function (error) {
      var grid = section.querySelector('[data-pw-brand-new-grid]');
      if (grid) grid.setAttribute('aria-busy', 'false');
      productStatus(section, 'Live products are temporarily unavailable. Use “View all” to continue shopping.');
      console.warn('[Preworn Brand] Products unavailable.', error);
    }).then(function () {
      state.loading = false;
      if (button) button.disabled = false;
    });
  }

  function activityAge(value) {
    var parsed = new Date(String(value || '').replace(' ', 'T'));
    if (!isFinite(parsed.getTime())) return 'RECENTLY';
    var minutes = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 60000));
    if (minutes < 1) return 'JUST NOW';
    if (minutes < 60) return minutes + ' MIN AGO';
    var hours = Math.floor(minutes / 60);
    return hours < 24 ? hours + (hours === 1 ? ' HOUR AGO' : ' HOURS AGO') : 'RECENTLY';
  }

  function safeActivityLink(section, value, title) {
    try {
      var parsed = new URL(value || '', window.location.origin);
      if (parsed.origin === window.location.origin) {
        applyScope(section, parsed.searchParams, true);
        return parsed.pathname + parsed.search + parsed.hash;
      }
    } catch (error) {}
    return searchHref(section, { q: title || brandName(section) });
  }

  function normaliseSoldCards(track, events) {
    var cards = track.querySelectorAll('.pw-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var event = events[i] || {};
      var removable = card.querySelectorAll('.pw-card__actions,.pw-card__discount,.pw-card__sold-context,.pw-card__sold-action');
      for (var r = 0; r < removable.length; r++) removable[r].remove();
      card.classList.add('pw-card--sold');
      var media = card.querySelector('.pw-card__media');
      if (media) media.insertAdjacentHTML('beforeend', '<div class="pw-card__discount pw-card__sold-badge"><span>SOLD</span></div>');
      var body = card.querySelector('.pw-card__body');
      if (body) body.insertAdjacentHTML('beforeend', '<div class="pw-card__sold-context">JUST BOUGHT · ' +
        escapeHtml(activityAge(event.occurred_at)) + '</div><span class="pw-card__sold-action">FIND SIMILAR <b aria-hidden="true">↗</b></span>');
    }
  }

  function normaliseNewCards(grid) {
    if (!grid || typeof grid.querySelectorAll !== 'function') return;
    var cards = grid.querySelectorAll('.pw-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      card.classList.add('pw-card--new');
      var media = card.querySelector('.pw-card__media');
      if (media && !media.querySelector('.pwbrandpage__new-badge, .pw-card__new-badge')) {
        media.insertAdjacentHTML(
          'beforeend',
          '<span class="pwbrandpage__new-badge" aria-label="New this week">NEW</span>'
        );
      }
    }
  }

  // Category pills, editorial cards and compact product tracks all retain
  // native touch scrolling while gaining click-and-drag support on desktop.
  function bindDragRail(target) {
    if (!target || target._pwBrandDragReady || typeof target.addEventListener !== 'function') return;
    target._pwBrandDragReady = true;
    var pressed = false;
    var dragged = false;
    var pointerId = null;
    var startX = 0;
    var startLeft = 0;

    target.addEventListener('pointerdown', function (event) {
      if ((event.pointerType && event.pointerType !== 'mouse') || event.button !== 0) return;
      pressed = true;
      dragged = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startLeft = target.scrollLeft;
    });
    target.addEventListener('pointermove', function (event) {
      if (!pressed || event.pointerId !== pointerId) return;
      var distance = event.clientX - startX;
      if (!dragged && Math.abs(distance) < 10) return;
      if (!dragged) {
        dragged = true;
        if (typeof target.setPointerCapture === 'function') {
          try { target.setPointerCapture(pointerId); } catch (error) {}
        }
      }
      if (target.classList) target.classList.add('is-dragging');
      target.scrollLeft = startLeft - distance;
      event.preventDefault();
    });
    function release(event) {
      if (!pressed || (event.pointerId != null && event.pointerId !== pointerId)) return;
      if (dragged) target._pwBrandSuppressClickUntil = Date.now() + 350;
      pressed = false;
      dragged = false;
      if (target.classList) target.classList.remove('is-dragging');
      if (typeof target.releasePointerCapture === 'function' && pointerId != null) {
        try { target.releasePointerCapture(pointerId); } catch (error) {}
      }
      pointerId = null;
    }
    target.addEventListener('pointerup', release);
    target.addEventListener('pointercancel', release);
    target.addEventListener('dragstart', function (event) { event.preventDefault(); });
    target.addEventListener('click', function (event) {
      if (Date.now() >= (target._pwBrandSuppressClickUntil || 0)) return;
      event.preventDefault();
      event.stopPropagation();
    });
  }

  function scrollEditorialRail(section, name, direction) {
    var selector = '[data-pw-brand-categories]';
    if (name === 'styles') selector = '[data-pw-brand-style-directory]';
    else if (name === 'sold') selector = '[data-pw-brand-sold-track]';
    var rail = section.querySelector(selector);
    if (!rail) return;
    var amount = Math.max(240, Math.round(rail.clientWidth * 0.82));
    if (typeof rail.scrollBy === 'function') {
      rail.scrollBy({ left: amount * direction, behavior: 'smooth' });
    } else {
      rail.scrollLeft += amount * direction;
    }
  }

  function renderSold(section, payload) {
    var track = section.querySelector('[data-pw-brand-sold-track]');
    var today = section.querySelector('[data-pw-brand-sold-today]');
    var shown = section.querySelector('[data-pw-brand-sold-shown]');
    var cards = renderer();
    var requestedKey = brandKey(scopeValue(section));
    var requestedTags = scopeTags(section);
    var events = payload && Array.isArray(payload.events) ? payload.events : [];
    var responseIsScoped = scopeKind(section) === 'tag'
      ? responseMatchesTagScope(payload, requestedTags)
      : payload && brandKey(payload.brand) === requestedKey;
    if (scopeKind(section) === 'brand') {
      events = events.filter(function (event) {
        return brandKey(event && event.vendor) === requestedKey;
      }).slice(0, 18);
    } else {
      // A response without the requested tag came from the pre-tag endpoint.
      // Filter its rich catalogue attributes exactly, or show nothing when an
      // exact comparison is impossible. Incorrect products are never safer.
      if (!responseIsScoped) {
        events = events.filter(function (event) {
          return eventMatchesTags(event, requestedTags);
        });
      }
      events = events.slice(0, 18);
    }
    updateTagSignalsFromActivity(section, payload, responseIsScoped);
    var scopedToday = responseIsScoped
      ? Math.max(0, Number(payload && payload.stats && payload.stats.items_today) || 0)
      : events.filter(function (event) {
        var occurred = new Date(String(event && event.occurred_at || '').replace(' ', 'T'));
        var now = new Date();
        return isFinite(occurred.getTime()) &&
          occurred.getUTCFullYear() === now.getUTCFullYear() &&
          occurred.getUTCMonth() === now.getUTCMonth() &&
          occurred.getUTCDate() === now.getUTCDate();
      }).length;
    if (today) today.textContent = scopedToday.toLocaleString('en-GB');
    if (shown) shown.textContent = events.length.toLocaleString('en-GB');
    if (!track || !cards || !events.length) {
      if (track) {
        track.innerHTML = '<p class="pwbrandpage__empty">No ' + escapeHtml(brandName(section)) +
          ' sales are available from the last 24 hours. Check back soon.</p>';
        track.setAttribute('aria-busy', 'false');
      }
      return;
    }
    var products = events.map(function (event, index) {
      var title = String(event.title || 'A one-off ' + brandName(section) + ' find');
      var image = safeImage(event.image_url);
      return {
        id: 'sold:' + String(event.id || index),
        handle: '',
        title: title,
        vendor: String(event.vendor || brandName(section)),
        productType: String(event.attributes && event.attributes.type || ''),
        availableForSale: false,
        onlineStoreUrl: safeActivityLink(section, event.similar_url, title),
        featuredImage: image ? { url: image, altText: title, width: null, height: null } : null,
        priceRange: { minVariantPrice: {
          amount: String(event.price || '0.00'),
          currencyCode: String(event.currency || 'GBP')
        } },
        compareAtPriceRange: { minVariantPrice: null },
        attributes: event.attributes || {}
      };
    });
    track.innerHTML = cards.renderList(products);
    normaliseSoldCards(track, events);
    if (scopeKind(section) === 'tag') bindDragRail(track);
    track.setAttribute('aria-busy', 'false');
  }

  function requestSold(section) {
    var endpoint = section.getAttribute('data-activity-endpoint');
    if (!endpoint) return Promise.resolve();
    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('format', 'json');
    applyScope(section, url.searchParams, false);
    url.searchParams.set('window_hours', '24');
    // Request a wider source pool so the browser guard can still recover a
    // useful exact-tag feed from an older unscoped activity endpoint.
    url.searchParams.set('limit', scopeKind(section) === 'tag' ? '50' : '18');
    return requestJson(url).then(function (payload) {
      if (!payload || payload.ok !== true) throw new Error('Invalid activity response');
      renderSold(section, payload);
    }).catch(function (error) {
      renderSold(section, { events: [], stats: { items_today: 0 } });
      console.warn('[Preworn Brand] Sold activity unavailable.', error);
    });
  }

  function initialise(section) {
    if (!section || section.getAttribute('data-pw-brand-ready') === 'true') return;
    section.setAttribute('data-pw-brand-ready', 'true');
    sampleHeroPalette(section);
    var grid = section.querySelector('[data-pw-brand-new-grid]');
    section._pwBrandProducts = {
      page: 1,
      limit: Math.max(12, Math.min(100,
        Number(section.getAttribute('data-products-per-page')) || 24)),
      windowDays: Math.max(7, Math.min(28, Number(section.getAttribute('data-new-in-days')) || 7)),
      total: 0,
      newInTotal: 0,
      newInTotalKnown: false,
      hasNext: false,
      fallback: false,
      category: '',
      loading: false,
      items: []
    };
    bindSizeRailMouseScroll(section);
    var loadMore = section.querySelector('[data-pw-brand-load-more]');
    if (loadMore) loadMore.addEventListener('click', function () {
      requestProducts(section, section._pwBrandProducts.page + 1, true);
    });
    var sliderButtons = section.querySelectorAll('[data-pw-brand-slider-button]');
    for (var sliderIndex = 0; sliderIndex < sliderButtons.length; sliderIndex++) {
      sliderButtons[sliderIndex].addEventListener('click', function () {
        scrollEditorialRail(
          section,
          this.getAttribute('data-pw-brand-slider-button'),
          Number(this.getAttribute('data-direction')) < 0 ? -1 : 1
        );
      });
    }
    var rankingTabLists = section.querySelectorAll('[data-pw-brand-ranking-tabs]');
    for (var rankingTabIndex = 0; rankingTabIndex < rankingTabLists.length; rankingTabIndex++) {
      rankingTabLists[rankingTabIndex].addEventListener('click', function (event) {
        var rankingTabs = event.currentTarget;
        var target = event.target;
        while (target && target !== rankingTabs &&
            (!target.getAttribute || !target.getAttribute('data-pw-brand-ranking-view'))) {
          target = target.parentNode;
        }
        if (!target || target === rankingTabs) return;
        section._pwBrandRankingInteracted = true;
        selectRankingView(section, target.getAttribute('data-pw-brand-ranking-view'));
      });
      rankingTabLists[rankingTabIndex].addEventListener('keydown', function (event) {
        var rankingTabs = event.currentTarget;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) === -1) return;
        var buttons = Array.prototype.slice.call(
          rankingTabs.querySelectorAll('[data-pw-brand-ranking-view]')
        );
        var index = buttons.indexOf(event.target);
        if (index < 0 || !buttons.length) return;
        event.preventDefault();
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = buttons.length - 1;
        else if (event.key === 'ArrowLeft') index = (index - 1 + buttons.length) % buttons.length;
        else index = (index + 1) % buttons.length;
        var key = buttons[index].getAttribute('data-pw-brand-ranking-view');
        section._pwBrandRankingInteracted = true;
        selectRankingView(section, key);
        var selected = rankingTabs.querySelector(
          '[data-pw-brand-ranking-view="' + key + '"]'
        );
        if (selected && selected.focus) selected.focus();
      });
    }
    if (!brandName(section) || !scopeValue(section) || !section.getAttribute('data-catalog-api')) {
      if (grid) grid.setAttribute('aria-busy', 'false');
      productStatus(section, 'This page needs a catalogue scope before live products can load.');
      return;
    }
    // The style directory is availability-led, so start its small dedicated
    // request first. It must never wait for historical sales aggregation.
    var styleRequest = requestStyleDirectory(section);
    var sizeRequest = requestSizeDirectory(section);
    // Keep the hero switcher independent of the broader metrics response.
    // Its current view remains usable while additional scoped views load.
    var rankingsRequest = requestRankingViews(section);
    // These datasets are independent. Start metrics and exact-scope activity
    // immediately instead of making them wait behind the larger product-grid
    // query. Both endpoints are cached and every request has bounded retry.
    var signalsRequest = requestSignals(section).then(function (hasLiveCategories) {
      section._pwBrandCategoriesReady = section._pwBrandCategoriesReady ||
        hasLiveCategories === true;
      return hasLiveCategories;
    });
    var soldRequest = requestSold(section);
    // Tag metrics include historical sales and can legitimately take longer
    // than the visible New In grid on a cold cache. Start the small, exact
    // category request immediately so its rail is never held behind them.
    var eagerFilterRequest = scopeKind(section) === 'tag'
      ? requestNewInFilterFallback(section) : Promise.resolve(false);
    var productsRequest = requestProducts(section, 1, false).then(function () {
      // Brand pages normally receive categories from their metrics response.
      // If that response is slower than the products, recover immediately.
      return requestNewInFilterFallback(section);
    });
    Promise.all([styleRequest, sizeRequest, rankingsRequest, signalsRequest, soldRequest, productsRequest, eagerFilterRequest]).then(function () {
      // Product cards and category metrics are independent requests. If the
      // metrics endpoint was busy or returned an incomplete cached payload,
      // recover the rail from the same exact scope and New In window via the
      // catalogue's lightweight facet-only response.
      return requestNewInFilterFallback(section);
    }).then(function () {
      if (!section._pwBrandCategoriesReady && section._pwBrandProducts.items.length) {
        renderCategories(section, []);
      }
    });
  }

  function initialiseAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll('[data-pw-brand-page]');
    for (var i = 0; i < sections.length; i++) initialise(sections[i]);
    if (scope.matches && scope.matches('[data-pw-brand-page]')) initialise(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialiseAll(document); });
  } else {
    initialiseAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initialiseAll(event.target);
  });
}());
